import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { fetchLandingUser } from "./fetchLandingUser";
import { LandingNavAuthed, LandingMobileAuthed } from "./LandingUserMenu";
import { NAV_LINKS, REGISTER_URL, SIGNIN_URL } from "./landingData";
import { prefetchHiwAndClaritaskPages } from "./prefetchPipeline";

const LOGO_DARK = "/images/claritick_tm_dark.svg";

/**
 * Rendered once in LandingApp.js, outside the Switch/Suspense that swaps page content —
 * stays mounted across every marketing-route navigation so only the content area (and footer,
 * which lives inside each page via LandingShell) goes blank while a chunk loads.
 */
const LandingNav = () => {
    const token = Cookies.get("access_token");
    const dispatch = useDispatch();
    const user = useSelector((state) => state.userReducer?.user);
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const isHome = location.pathname === "/";

    useEffect(() => {
        if (token) dispatch(fetchLandingUser());
    }, [token, dispatch]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [menuOpen]);

    useEffect(() => {
        if (location.pathname !== "/") {
            window.scrollTo(0, 0);
            return;
        }

        if (location.hash) {
            const scrollToHash = () => {
                const el = document.querySelector(location.hash);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    return true;
                }
                return false;
            };
            if (!scrollToHash()) {
                requestAnimationFrame(() => {
                    if (!scrollToHash()) {
                        window.scrollTo(0, 0);
                    }
                });
            }
            return;
        }

        window.scrollTo(0, 0);
    }, [location.pathname, location.hash]);

    const scrollTo = useCallback((e, href) => {
        if (!href.startsWith("#")) return;
        e.preventDefault();
        setMenuOpen(false);
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const warmPageImages = useCallback((path) => {
        if (path === "/how-it-works" || path === "/claritask") {
            prefetchHiwAndClaritaskPages();
        }
    }, []);

    const isNavActive = useCallback((l) => {
        if (l.hash) {
            return location.pathname === l.path && location.hash === l.hash;
        }
        return location.pathname === l.path;
    }, [location.pathname, location.hash]);

    const isBlogActive = location.pathname === "/blog" || location.pathname.startsWith("/blog/");

    const renderNavLink = (l) => {
        const isActive = isNavActive(l);
        const className = isActive ? "is-active" : undefined;
        const ariaCurrent = isActive ? "page" : undefined;
        const onWarm = () => warmPageImages(l.path);

        if (isHome && l.path === "/" && l.hash) {
            return (
                <a key={l.label} href={l.hash} className={className} aria-current={ariaCurrent} onClick={(e) => scrollTo(e, l.hash)}>
                    {l.label}
                </a>
            );
        }

        if (l.hash) {
            return (
                <Link key={l.label} to={`${l.path}${l.hash}`} className={className} aria-current={ariaCurrent} onClick={() => setMenuOpen(false)}>
                    {l.label}
                </Link>
            );
        }

        return (
            <Link
                key={l.label}
                to={l.path}
                className={className}
                aria-current={ariaCurrent}
                onMouseEnter={onWarm}
                onFocus={onWarm}
                onTouchStart={onWarm}
                onClick={() => setMenuOpen(false)}
            >
                {l.label}
            </Link>
        );
    };

    return (
        <>
            <nav className="clk-nav clk-nav-scrolled">
                <div className="clk-container clk-nav-inner">
                    <Link to="/" aria-label="Claritick home">
                        <img
                            src={LOGO_DARK}
                            alt="Claritick"
                            className="clk-nav-logo clk-nav-logo-dark"
                            loading="eager"
                            fetchPriority="high"
                            decoding="sync"
                        />
                    </Link>
                    <ul className="clk-nav-links">
                        {NAV_LINKS.map((l) => (
                            <li key={l.label}>{renderNavLink(l)}</li>
                        ))}
                        <li>
                            <Link to="/blog" className={isBlogActive ? "is-active" : undefined} onClick={() => setMenuOpen(false)}>
                                Blog
                            </Link>
                        </li>
                    </ul>
                    <div className="clk-nav-cta">
                        {token ? (
                            <LandingNavAuthed user={user} />
                        ) : (
                            <>
                                <a className="clk-nav-signin" href={SIGNIN_URL}>Sign in</a>
                                <a className="clk-btn clk-btn-primary" href={REGISTER_URL}>New Account</a>
                            </>
                        )}
                    </div>
                    <button
                        type="button"
                        className="clk-nav-toggle"
                        aria-label="Open menu"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(true)}
                    >
                        <span /><span /><span />
                    </button>
                </div>
            </nav>

            <div className={`clk-mobile-overlay${menuOpen ? " open" : ""}`} onClick={() => setMenuOpen(false)} />
            <div className={`clk-mobile-panel${menuOpen ? " open" : ""}`}>
                <button type="button" className="clk-mobile-close" aria-label="Close menu" onClick={() => setMenuOpen(false)}>×</button>
                {NAV_LINKS.map((l) => renderNavLink(l))}
                <Link to="/blog" className={isBlogActive ? "is-active" : undefined} onClick={() => setMenuOpen(false)}>Blog</Link>
                {token ? (
                    <LandingMobileAuthed user={user} onClose={() => setMenuOpen(false)} />
                ) : (
                    <div className="clk-mobile-actions">
                        <a className="clk-btn clk-btn-outline" href={SIGNIN_URL}>Sign in</a>
                        <a className="clk-btn clk-btn-primary" href={REGISTER_URL}>New Account</a>
                    </div>
                )}
            </div>
        </>
    );
};

export default LandingNav;
